import React, { useState, useEffect } from 'react';
import { 
    CheckSquare, Square, Download, Upload, Trash2, Edit, 
    Users, Calendar, Package, FileText, AlertTriangle, CheckCircle
} from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';

const BulkOperations = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [selectedOperation, setSelectedOperation] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [operationData, setOperationData] = useState({
        patients: [],
        appointments: [],
        staff: [],
        inventory: []
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [patients, appointments, staff, inventory] = await Promise.all([
                apiService.getPatients({ organizationId: currentUser.organizationId }),
                apiService.getAppointments({ organizationId: currentUser.organizationId }),
                apiService.getStaff({ organizationId: currentUser.organizationId }),
                apiService.getInventory({ organizationId: currentUser.organizationId })
            ]);

            setOperationData({
                patients: patients.filter(p => !p.is_deleted),
                appointments: appointments.filter(a => !a.is_deleted),
                staff: staff.filter(s => !s.is_deleted),
                inventory: inventory.filter(i => !i.is_deleted)
            });
        } catch (error) {
            console.error('Error fetching data:', error);
            showAlert('Failed to load data for bulk operations', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleItemSelect = (itemId, type) => {
        const itemKey = `${type}-${itemId}`;
        setSelectedItems(prev => 
            prev.includes(itemKey) 
                ? prev.filter(id => id !== itemKey)
                : [...prev, itemKey]
        );
    };

    const handleSelectAll = (type) => {
        const items = operationData[type];
        const allKeys = items.map(item => `${type}-${item.id}`);
        const allSelected = allKeys.every(key => selectedItems.includes(key));
        
        if (allSelected) {
            setSelectedItems(prev => prev.filter(key => !key.startsWith(`${type}-`)));
        } else {
            setSelectedItems(prev => [...prev, ...allKeys.filter(key => !prev.includes(key))]);
        }
    };

    const getSelectedItemsByType = (type) => {
        return selectedItems
            .filter(key => key.startsWith(`${type}-`))
            .map(key => key.split('-')[1]);
    };

    const handleBulkOperation = async () => {
        if (selectedItems.length === 0) {
            showAlert('Please select items to perform bulk operations', 'warning');
            return;
        }

        if (!selectedOperation) {
            showAlert('Please select an operation to perform', 'warning');
            return;
        }

        setLoading(true);
        try {
            const results = await performBulkOperation(selectedOperation, selectedItems);
            showAlert(`Bulk operation completed: ${results.success} successful, ${results.failed} failed`, 'success');
            
            // Refresh data
            await fetchData();
            setSelectedItems([]);
            setSelectedOperation('');
        } catch (error) {
            console.error('Error performing bulk operation:', error);
            showAlert('Failed to perform bulk operation', 'error');
        } finally {
            setLoading(false);
        }
    };

    const performBulkOperation = async (operation, items) => {
        let success = 0;
        let failed = 0;

        for (const itemKey of items) {
            try {
                const [type, id] = itemKey.split('-');
                
                switch (operation) {
                    case 'export':
                        // Export logic would be handled separately
                        success++;
                        break;
                        
                    case 'delete':
                        await deleteItem(type, id);
                        success++;
                        break;
                        
                    case 'update_status':
                        await updateItemStatus(type, id);
                        success++;
                        break;
                        
                    case 'send_notifications':
                        await sendNotification(type, id);
                        success++;
                        break;
                        
                    default:
                        failed++;
                }
            } catch (error) {
                console.error(`Error processing item ${itemKey}:`, error);
                failed++;
            }
        }

        return { success, failed };
    };

    const deleteItem = async (type, id) => {
        switch (type) {
            case 'patients':
                await apiService.deletePatient(id);
                break;
            case 'appointments':
                await apiService.deleteAppointment(id);
                break;
            case 'staff':
                await apiService.deleteStaff(id);
                break;
            case 'inventory':
                await apiService.deleteInventoryItem(id);
                break;
        }
    };

    const updateItemStatus = async (type, id) => {
        // Update status based on type
        switch (type) {
            case 'patients':
                await apiService.updatePatient({ id, status: 'archived' });
                break;
            case 'appointments':
                await apiService.updateAppointment({ id, status: 'completed' });
                break;
            case 'staff':
                await apiService.updateStaff({ id, isActive: false });
                break;
            case 'inventory':
                await apiService.updateInventoryItem({ id, status: 'inactive' });
                break;
        }
    };

    const sendNotification = async (type, id) => {
        // Send notification based on type
        console.log(`Sending notification for ${type} ${id}`);
        // Implementation would depend on notification system
    };

    const exportSelectedItems = () => {
        const exportData = [];
        
        selectedItems.forEach(itemKey => {
            const [type, id] = itemKey.split('-');
            const item = operationData[type].find(i => i.id.toString() === id);
            if (item) {
                exportData.push({
                    type,
                    ...item
                });
            }
        });

        const csvContent = convertToCSV(exportData);
        downloadCSV(csvContent, `bulk-export-${new Date().toISOString().split('T')[0]}.csv`);
        showAlert('Data exported successfully', 'success');
    };

    const convertToCSV = (data) => {
        if (data.length === 0) return '';
        
        const headers = Object.keys(data[0]);
        const csvRows = [headers.join(',')];
        
        data.forEach(row => {
            const values = headers.map(header => {
                const value = row[header];
                return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
            });
            csvRows.push(values.join(','));
        });
        
        return csvRows.join('\n');
    };

    const downloadCSV = (content, filename) => {
        const blob = new Blob([content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const BulkOperationCard = ({ title, type, icon: Icon, data, color }) => {
        const selectedCount = getSelectedItemsByType(type).length;
        const allSelected = data.length > 0 && selectedCount === data.length;
        const someSelected = selectedCount > 0 && selectedCount < data.length;

        return (
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${color}`}>
                                <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">{title}</h3>
                                <p className="text-sm text-gray-600">{data.length} items</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleSelectAll(type)}
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                                {allSelected ? <CheckSquare className="w-4 h-4" /> : 
                                 someSelected ? <CheckSquare className="w-4 h-4" /> : 
                                 <Square className="w-4 h-4" />}
                                {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                    </div>
                </div>
                
                <div className="p-6">
                    {data.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>No {type} found</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {data.slice(0, 10).map((item) => {
                                const itemKey = `${type}-${item.id}`;
                                const isSelected = selectedItems.includes(itemKey);
                                
                                return (
                                    <div 
                                        key={item.id}
                                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                            isSelected ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => handleItemSelect(item.id, type)}
                                    >
                                        <div className="flex items-center gap-3">
                                            {isSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : 
                                             <Square className="w-4 h-4 text-gray-400" />}
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-900">
                                                    {item.name || item.title || `${type} #${item.id}`}
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    {type === 'patients' && `${item.age ? item.age + ' years' : 'Age not specified'} • ${item.phone}`}
                                                    {type === 'appointments' && `${new Date(item.appointmentTime).toLocaleDateString()} • ${item.department}`}
                                                    {type === 'staff' && `${item.role} • ${item.department}`}
                                                    {type === 'inventory' && `${item.quantity} in stock • $${item.price}`}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {data.length > 10 && (
                                <div className="text-center py-2 text-sm text-gray-500">
                                    ... and {data.length - 10} more items
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading && operationData.patients.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Bulk Operations</h2>
                    <p className="text-gray-600">Perform operations on multiple items at once</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={fetchData}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Upload className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Operation Controls */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Operations</h3>
                <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Operation
                        </label>
                        <select
                            value={selectedOperation}
                            onChange={(e) => setSelectedOperation(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Choose an operation...</option>
                            <option value="export">Export Selected Items</option>
                            <option value="update_status">Update Status</option>
                            <option value="send_notifications">Send Notifications</option>
                            <option value="delete">Delete Selected Items</option>
                        </select>
                    </div>
                    <div className="flex items-end gap-2">
                        <button
                            onClick={exportSelectedItems}
                            disabled={selectedItems.length === 0}
                            className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className="w-4 h-4" />
                            Export ({selectedItems.length})
                        </button>
                        <button
                            onClick={handleBulkOperation}
                            disabled={selectedItems.length === 0 || !selectedOperation || selectedOperation === 'export'}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <CheckCircle className="w-4 h-4" />
                            Execute ({selectedItems.length})
                        </button>
                    </div>
                </div>
                
                {selectedItems.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                            <strong>{selectedItems.length}</strong> items selected for bulk operation
                        </p>
                    </div>
                )}
            </div>

            {/* Data Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BulkOperationCard
                    title="Patients"
                    type="patients"
                    icon={Users}
                    data={operationData.patients}
                    color="bg-blue-500"
                />
                
                <BulkOperationCard
                    title="Appointments"
                    type="appointments"
                    icon={Calendar}
                    data={operationData.appointments}
                    color="bg-green-500"
                />
                
                <BulkOperationCard
                    title="Staff"
                    type="staff"
                    icon={Users}
                    data={operationData.staff}
                    color="bg-purple-500"
                />
                
                <BulkOperationCard
                    title="Inventory"
                    type="inventory"
                    icon={Package}
                    data={operationData.inventory}
                    color="bg-orange-500"
                />
            </div>

            {/* Warning for destructive operations */}
            {selectedOperation === 'delete' && selectedItems.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <h4 className="font-semibold text-red-800">Warning: Destructive Operation</h4>
                    </div>
                    <p className="text-sm text-red-700 mt-2">
                        You are about to permanently delete {selectedItems.length} items. This action cannot be undone.
                    </p>
                </div>
            )}
        </div>
    );
};

export default BulkOperations;
